import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ScrollButton from "../../ScrollButton";
import QnASideBarRight from "../questionanswer/QnASideBarRight";
import BlogSideBarRight from "../blogs/BlogSideBarRight";

/**
 * Projects — professional gallery of sample projects
 * - Hero header + subtitle
 * - Search + tag filters (chips)
 * - Modern responsive card grid (Bootstrap 5)
 * - Each card shows: difficulty, short description, tech stack, repo/demo links
 * - Left: Blog sidebar (sticky); Right: Q&A sidebar
 */

const PROJECTS = [
  {
    title: "Movie Catalog (Spring + REST)",
    level: "Intermediate",
    excerpt:
      "Cleanly structured Spring Boot service exposing REST endpoints for movie search, ratings, and catalog operations.",
    tech: ["Java", "Spring Boot", "REST", "JPA", "PostgreSQL", "JavaScript"],
    repo: "https://github.com/rbnpantha/movie",
    demo: null,
  },
  {
    title: "Hotel Reservation System",
    level: "Intermediate",
    excerpt:
      "End‑to‑end booking flow with admin room management, email confirmations, and user profiles.",
    tech: ["Spring Boot", "Spring Data JPA", "REST", "Thymeleaf/JSP", "Bootstrap", "Mail"],
    repo: "https://github.com/rbnpantha/reservationsystem",
    demo: null,
  },
  {
    title: "Palindrome Calculator (JavaFX)",
    level: "Beginner",
    excerpt:
      "Desktop app that computes the largest palindrome within user‑defined bounds using an intuitive JavaFX UI.",
    tech: ["Java", "JavaFX"],
    repo: "https://github.com/rbnpantha/PalindromeApplication",
    demo: null,
  },
  {
    title: "Twitter‑Bar (Trends Dashboard)",
    level: "Advanced",
    excerpt:
      "Single‑page app surfacing real‑time Twitter topics with geolocation overlays and curated feeds.",
    tech: ["Java", "JSP/Servlet", "Twitter API", "Google Maps", "NetBeans", "GlassFish"],
    repo: "https://github.com/rbnpantha/BlogApp/tree/master/blog",
    demo: null,
  },
];

const allTags = Array.from(new Set(PROJECTS.flatMap((p) => p.tech))).sort();

function useFilteredProjects(query, activeTags) {
  const q = query.trim().toLowerCase();
  return useMemo(() => {
    return PROJECTS.filter((p) => {
      const matchesQ = !q || `${p.title} ${p.excerpt} ${p.tech.join(" ")}`.toLowerCase().includes(q);
      const matchesTags = activeTags.size === 0 || p.tech.some((t) => activeTags.has(t));
      return matchesQ && matchesTags;
    });
  }, [q, activeTags]);
}

function TagChips({ activeTags, setActiveTags }) {
  const toggle = (t) => {
    const next = new Set(activeTags);
    next.has(t) ? next.delete(t) : next.add(t);
    setActiveTags(next);
  };

  return (
    <div className="d-flex flex-wrap gap-2">
      {allTags.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => toggle(t)}
          className={`btn btn-sm ${activeTags.has(t) ? "btn-primary" : "btn-outline-secondary"}`}
          aria-pressed={activeTags.has(t) ? "true" : "false"}
          aria-label={`Filter by ${t}`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

function FilterBar({ value, onChange, total, shown, activeTags, setActiveTags }) {
  return (
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-body">
        <form className="d-flex flex-wrap align-items-center gap-2" role="search" aria-label="Project search">
          <input
            type="search"
            className="form-control flex-grow-1"
            placeholder="Search projects (e.g., Spring, REST, JavaFX, Twitter)"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-label="Search projects"
          />
          <button type="button" className="btn btn-outline-secondary" onClick={() => onChange("")}>Clear</button>
        </form>
        <div className="mt-3">
          <TagChips activeTags={activeTags} setActiveTags={setActiveTags} />
        </div>
        <div className="small text-muted mt-2">Showing {shown} of {total}</div>
      </div>
    </div>
  );
}

function LevelBadge({ level }) {
  const map = {
    Beginner: "bg-success",
    Intermediate: "bg-warning text-dark",
    Advanced: "bg-danger",
  };
  return <span className={`badge ${map[level] || "bg-secondary"}`}>{level}</span>;
}

function ProjectCard({ project }) {
  return (
    <div className="col">
      <div className="card h-100 shadow-sm border-0">
        <div className="card-body d-flex flex-column">
          <div className="d-flex align-items-center justify-content-between mb-1">
            <LevelBadge level={project.level} />
            <div className="small text-muted">Open Source</div>
          </div>
          <h3 className="h5 mb-2">{project.title}</h3>
          <p className="text-body-secondary">{project.excerpt}</p>
          <div className="mt-auto pt-2">
            <div className="d-flex flex-wrap gap-1 mb-2">
              {project.tech.map((t) => (
                <span key={t} className="badge bg-light text-muted border">{t}</span>
              ))}
            </div>
            <div className="d-flex gap-2">
              {project.repo && (
                <a
                  href={project.repo}
                  className="btn btn-sm btn-outline-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Repo
                </a>
              )}
              {project.demo && (
                <a
                  href={project.demo}
                  className="btn btn-sm btn-outline-secondary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Live Demo
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  const [query, setQuery] = useState("");
  const [activeTags, setActiveTags] = useState(new Set());

  const filtered = useFilteredProjects(query, activeTags);

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: Blog sidebar (sticky) */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <BlogSideBarRight />
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          <header className="mb-3">
            <h1 className="h2 mb-2">Sample Projects</h1>
            <p className="text-muted mb-0">
              Explore practical, well‑structured projects you can learn from or extend. Filter by tech stack, read the code, and adapt to your needs.
            </p>
          </header>

          <FilterBar
            value={query}
            onChange={setQuery}
            total={PROJECTS.length}
            shown={filtered.length}
            activeTags={activeTags}
            setActiveTags={setActiveTags}
          />

          <section aria-labelledby="projects-grid-heading" className="mb-4">
            <h2 id="projects-grid-heading" className="visually-hidden">Projects</h2>
            <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3 g-md-4">
              {filtered.map((p) => (
                <ProjectCard key={p.title} project={p} />
              ))}
            </div>
          </section>

          <section className="mb-5" aria-labelledby="cta-heading">
            <div className="card shadow-sm border-0">
              <div className="card-body p-4">
                <div className="row g-3 align-items-center">
                  <div className="col-12 col-md">
                    <h2 id="cta-heading" className="h5 mb-1">Have a project to showcase?</h2>
                    <p className="text-muted mb-0">Open a pull request or send us a link—we love featuring community builds.</p>
                  </div>
                  <div className="col-12 col-md-auto">
                    <Link to="/contact" className="btn btn-primary">Submit a project</Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="alert alert-info small" role="note">
            <strong>Tip:</strong> When you fork a project, add CI (tests & lint), a containerized dev env, and a short README with setup steps and architecture notes.
          </div>

          <ScrollButton />
        </main>

        {/* RIGHT: Q&A sidebar (sticky) */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <QnASideBarRight />
        </aside>
      </div>
    </div>
  );
}
